import { Badge, Check, Close, Minus, Warning } from '@react-apps/design-system';
import type { IconProps } from '@react-apps/design-system';
import { CopyButton } from '@/shared/components/copy-button';
import { Tooltip } from '@/shared/components/tooltip';
import type {
  EmailMessage,
  EmailStatus,
  EmailType,
} from '../domain/entities/notify.entity';

const STATUS_META: Record<
  EmailStatus,
  {
    label: string;
    description: string;
    icon: React.ComponentType<IconProps>;
    classes: string;
  }
> = {
  QUEUED: {
    label: 'Na fila',
    description: 'Na fila — aguardando envio.',
    icon: Minus,
    classes: 'bg-blue-100 text-blue-800',
  },
  SENT: {
    label: 'Enviado',
    description: 'Enviado com sucesso.',
    icon: Check,
    classes: 'bg-green-100 text-green-800',
  },
  FAILED: {
    label: 'Falhou',
    description: 'Falha no envio — veja a coluna Situação para detalhes.',
    icon: Close,
    classes: 'bg-red-100 text-red-800',
  },
  DISCARDED: {
    label: 'Descartado',
    description: 'Descartado — endereço de e-mail inválido.',
    icon: Warning,
    classes: 'bg-yellow-100 text-yellow-800',
  },
};

/**
 * Ícone colorido por status, com tooltip explicando o que ele significa.
 * Para FAILED/DISCARDED, o tooltip traz o motivo detalhado e o botão
 * "Copiar detalhes" — antes uma coluna própria ("Situação"), agora
 * condensada aqui para não competir por espaço na tabela.
 */
export function EmailStatusIcon({ email }: { email: EmailMessage }) {
  const meta = STATUS_META[email.status];
  if (!meta) return <span>{email.status}</span>;
  const StatusIcon = meta.icon;
  const presentation = getEmailErrorPresentation(email);

  return (
    <Tooltip
      message={
        presentation ? (
          <div className="space-y-2">
            <p
              className={
                presentation.retryable
                  ? 'font-medium text-red-600 dark:text-red-400'
                  : 'font-medium text-amber-600 dark:text-amber-500'
              }
            >
              {presentation.label}
            </p>
            <p>{presentation.description}</p>
            <CopyButton
              text={buildEmailErrorReport(email)}
              label="Copiar detalhes"
            />
          </div>
        ) : (
          meta.description
        )
      }
      triggerLabel={`Status: ${meta.label}`}
      triggerClassName={`inline-flex h-8 w-8 items-center justify-center rounded-full transition hover:opacity-80 ${meta.classes}`}
    >
      <StatusIcon size="sm" />
    </Tooltip>
  );
}

const TYPE_LABELS: Record<EmailType, string> = {
  first_login: 'Primeiro acesso',
  password_reset: 'Redefinição de senha',
  email_verification: 'Verificação de e-mail',
};

export function getEmailTypeLabel(type: EmailType): string {
  return TYPE_LABELS[type] ?? type;
}

export function EmailTypeBadge({ type }: { type: EmailType }) {
  return <Badge variant="default">{getEmailTypeLabel(type)}</Badge>;
}

export function getApiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const maybe = err as {
      response?: { data?: { message?: unknown } };
      message?: unknown;
    };
    const apiMsg = maybe.response?.data?.message;
    if (typeof apiMsg === 'string') return apiMsg;
    if (Array.isArray(apiMsg) && typeof apiMsg[0] === 'string') {
      return apiMsg[0];
    }
    if (typeof maybe.message === 'string') return maybe.message;
  }
  return fallback;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

/** Texto de erro inline padronizado (claro + escuro) — usado pelo `ConfirmModal`. */
export function ErrorText({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-red-600 dark:text-red-400">{children}</p>;
}

export interface EmailErrorPresentation {
  /** Rótulo curto, para quem não é técnico entender de cara o tipo do problema. */
  label: string;
  /** Explicação em linguagem simples do que fazer a respeito. */
  description: string;
  /** `false` para DISCARDED (destinatário inválido) — não há o que reprocessar. */
  retryable: boolean;
}

/**
 * Traduz `status`/`lastError` num texto que orienta quem não é técnico: se o
 * problema é de negócio (destinatário errado — a pessoa mesma resolve
 * corrigindo o cadastro) ou técnico (reprocessa; se falhar de novo, reporta
 * pro time de dev com os detalhes).
 */
export function getEmailErrorPresentation(
  email: EmailMessage,
): EmailErrorPresentation | null {
  if (!email.lastError) return null;
  if (email.status === 'DISCARDED') {
    return {
      label: 'Endereço de e-mail inválido',
      description:
        'O e-mail do destinatário está incorreto ou incompleto — provavelmente digitado errado na compra/cadastro. Corrija o e-mail do cliente; não é um problema técnico.',
      retryable: false,
    };
  }
  return {
    label: 'Falha técnica no envio',
    description:
      'Pode ser uma instabilidade temporária. Clique em reprocessar; se falhar de novo, copie os detalhes e reporte ao time de desenvolvimento.',
    retryable: true,
  };
}

/** Texto pronto para colar num ticket/mensagem ao time de dev. */
export function buildEmailErrorReport(email: EmailMessage): string {
  return [
    `E-mail ID: ${email.id}`,
    `Tipo: ${getEmailTypeLabel(email.type)}`,
    `Destinatário: ${email.recipient}`,
    `Status: ${email.status}`,
    `Tentativas: ${email.attempts}`,
    `Erro: ${email.lastError ?? '—'}`,
    `Criado em: ${formatDateTime(email.createdAt)}`,
  ].join('\n');
}
